import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import GeneralIcon from "../../../../assets";
import PageLoader from "../../../../components/PageLoader";
import { fetchBalances } from "../../../../services/balancesService";

export default function Faturamento() {
	const navigate = useNavigate();
	const [tab, setTab] = useState("saldo"); // "saldo" | "historico"
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [data, setData] = useState(null);
	const [month, setMonth] = useState("Outubro");
	const [year, setYear] = useState("2025");

	useEffect(() => {
		let mounted = true;
		(async () => {
			setLoading(true);
			setError("");
			try {
				const balances = await fetchBalances();
				if (mounted) setData(balances);
			} catch (e) {
				if (mounted) setError("Falha ao carregar faturamento");
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => { mounted = false; };
	}, []);

  const youCanStillWithdraw = useMemo(() => {
    if (!data) return 0;
    const currentKey = data.currentLimitKey || "PF";
    const monthlyLimit = data.limits[currentKey];
    if (!monthlyLimit) return null; // unlimited
    const already = Math.min(data.totalWithdrawn, monthlyLimit);
    return Math.max(monthlyLimit - already, 0);
  }, [data]);	return (
		<PageLoader loading={loading}>
			<div className="max-w-md mx-auto px-4 py-4">
				<div className="flex items-center gap-2 mb-4">
					<button className="text-gray-700" onClick={() => navigate(-1)}>&larr;</button>
					<h1 className="text-lg font-semibold">Faturamento</h1>
				</div>

				<div className="flex gap-2 mb-4">
					<button
						className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'saldo' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'}`}
						onClick={() => setTab("saldo")}
					>
						Saldo
					</button>
					<button
						className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'historico' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'}`}
						onClick={() => setTab("historico")}
					>
						Histórico
					</button>
				</div>

				{error && (
					<p className="text-sm text-red-600 mb-3">{error}</p>
				)}

				{tab === "saldo" && data && (
					<div className="space-y-6">
						{/* Saldo disponível */}
						<div>
							<div className="flex justify-between items-center mb-2">
								<h2 className="text-sm font-semibold text-gray-800">Saldo disponível</h2>
								<button className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold">Sacar</button>
							</div>
							<p className="text-3xl font-bold">R$ {data.available.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
							<p className="text-sm text-gray-600 mt-1">Este é o saldo que pode ser sacado pelo vendedor.</p>
						</div>

						{/* Alert */}
						<div className="rounded-lg bg-orange-50 border border-orange-200 p-3">
							<p className="text-xs font-semibold text-orange-600 mb-1">IMPORTANTE</p>
							<p className="text-sm text-orange-600">
								Atente-se que, por motivos fiscais, o valor total que pode ser sacado por mês pelo vendedor é limitado pelo "teto de saques por mês" respectivo ao porte da loja.
							</p>
						</div>

						{/* Saldo retido */}
						<div>
							<h2 className="text-sm font-semibold text-gray-800 mb-1">Saldo retido</h2>
							<p className="text-3xl font-bold">R$ {data.retained.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
							<p className="text-sm text-gray-600 mt-1">
								É o valor de todos pedidos cuja o ambiente de pagamento é "Aplicativo" (pagamento intermediado pelo Geppro) que ainda podem ser estornados.
							</p>
						</div>

						{/* Total já sacado + limites */}
						<div className="space-y-2">
							<div className="flex justify-between items-end">
								<div>
									<h2 className="text-sm font-semibold text-gray-800">Valor total já sacado</h2>
									<p className="text-3xl font-bold">R$ {data.totalWithdrawn.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
									<p className="text-sm text-gray-600 mt-1">Este é o valor que já foi sacado pelo vendedor.</p>
								</div>
								<div className="text-right">
									{youCanStillWithdraw === null ? (
										<p className="text-xs text-green-600">Você tem saques ilimitados</p>
									) : (
										<p className="text-xs text-green-600">Você ainda tem R$ {youCanStillWithdraw.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} para sacar</p>
									)}
								</div>
							</div>

              <div className="text-sm text-gray-800 space-y-1">
                <div className={`flex justify-between ${data.currentLimitKey === 'PF' ? 'text-purple-600' : ''}`}><span>Pessoa Física (PF)</span><span>R$ {data.limits.PF.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></div>
                <div className={`flex justify-between ${data.currentLimitKey === 'MEI' ? 'text-purple-600' : ''}`}><span>Microempreendedor Individual (MEI)</span><span>R$ {data.limits.MEI.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></div>
                <div className={`flex justify-between ${data.currentLimitKey === 'ME' ? 'text-purple-600' : ''}`}><span>Microempresa (ME)</span><span>R$ {data.limits.ME.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></div>
                <div className={`flex justify-between ${data.currentLimitKey === 'EPP' ? 'text-purple-600' : ''}`}><span>Empresa de Pequeno Porte (EPP)</span><span>R$ {data.limits.EPP.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></div>
                <div className={`flex justify-between ${data.currentLimitKey === 'EMP_EGP' ? 'text-purple-600' : ''}`}><span>Empresa de Médio e Grande Porte (EMP e EGP)</span><span>{data.limits.EMP_EGP ? (`R$ ${data.limits.EMP_EGP.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`) : 'Ilimitado'}</span></div>
              </div>							<button className="mt-2 w-full bg-purple-600 text-white py-3 rounded-lg font-semibold">Atualizar porte da loja</button>
						</div>
					</div>
				)}

				{tab === "historico" && data && (
					<div className="space-y-6">
						{/* Saldo total mensal */}
						<div>
							<h2 className="text-sm font-semibold text-gray-800">Saldo total mensal</h2>
							<p className="text-3xl font-bold">R$ {data.currentMonthTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
							<p className="text-sm text-gray-600 mt-1">Este é o valor total de saldo disponível ganho no mês de {month.toLowerCase()} (atual).</p>
						</div>

						{/* Filtros */}
						<div className="flex gap-2">
							<select className="flex-1 bg-gray-100 rounded-lg h-10 px-3 text-sm" value={month} onChange={(e) => setMonth(e.target.value)}>
								{['Outubro','Novembro','Dezembro'].map(m => <option key={m} value={m}>{m}</option>)}
							</select>
							<select className="flex-1 bg-gray-100 rounded-lg h-10 px-3 text-sm" value={year} onChange={(e) => setYear(e.target.value)}>
								{['2025','2024','2023'].map(y => <option key={y} value={y}>{y}</option>)}
							</select>
						</div>
						<button className="w-full bg-purple-600 text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm">
							<GeneralIcon iconName="search" className="w-4 h-4" />
							Buscar
						</button>

						{/* Histórico de saques */}
						<div>
							<h3 className="text-sm font-semibold text-gray-800 mb-2">Histórico de saques</h3>
							{data.withdrawals && data.withdrawals.length > 0 ? (
								<div className="space-y-2">
									{data.withdrawals.map((w) => (
										<div key={w.id} className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div className="bg-gray-100 rounded-md px-2 py-1 text-xs">Pix</div>
												<div className="text-sm">
													<div className="font-medium">{w.date}</div>
													<div className="text-gray-700">Saque de R$ {w.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
												</div>
											</div>
											<button className="text-purple-600 hover:text-purple-700">
												<GeneralIcon iconName="download" className="w-5 h-5" />
											</button>
										</div>
									))}
								</div>
							) : (
								<div className="text-center mt-2">
									<GeneralIcon iconName="banco" className="w-20 h-20 mx-auto mb-2" />
									<p className="font-semibold">Vazio!</p>
									<p className="text-sm text-gray-600">Você não realizou saques neste mês.</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</PageLoader>
	);
}

